/**
 * Disambiguating human labels that are not unique.
 *
 * THE PRINCIPLE (product owner, 2026-08-05): **a name is a human label; an id
 * is an identity.** Production and department names are user-editable, change
 * over time, and are NOT unique — duplicates are legitimate and expected. Any
 * buyer may create a production, two buyers may independently create
 * "Deep Water", a single production may contain two departments both called
 * "Props", and `createPersonalSpace` gives every user a production AND a
 * department both named "My Space", so duplicate names are already the
 * commonest shape in the database.
 *
 * The system must be INDIFFERENT to that, never defensive: no duplicate
 * warning, no reconciliation prompt — *"each is its own island and doesnt care
 * about the other"*. The only thing that has to change is that a human looking
 * at a list can tell two entries apart. The owner's ruling for how:
 * *"append the department ID the user will quickly figure out which is wich"*.
 *
 * So this module answers exactly one question — **which entries in THIS list
 * collide?** — and formats the suffix in exactly one place. It is deliberately
 * CONDITIONAL: a list with no collision is rendered completely unchanged, so
 * ids never appear for the overwhelming majority of users.
 *
 * Scope note: collision is always relative to the list being shown (the user's
 * own departments, the productions in the orders on screen). This is a DISPLAY
 * aid, not a system-wide uniqueness mechanism — the id remains the sole identity
 * in the data model whether it is on screen or not.
 */

/**
 * Fold a label the way a human eye does before comparing. Trailing whitespace
 * and a difference of case do not make two entries distinguishable on screen,
 * so they must not count as distinguishing here either.
 */
const foldLabel = (value) => String(value ?? '').trim().toLowerCase();

/**
 * Which ids in `entries` share their collision key with at least one other
 * entry?
 *
 * @param {Array<{id: *, key: *}>} entries — `id` is the stable identity,
 *   `key` is the human text being checked for collisions. The caller chooses
 *   `key`, because what "looks the same" differs per surface: the department
 *   switcher collides on department NAME, an order filter on the whole
 *   rendered production label.
 * @returns {Set} the colliding ids. Empty when every key is distinct.
 */
export function collidingIds(entries) {
  const counts = new Map();
  entries.forEach(({ key }) => {
    const folded = foldLabel(key);
    counts.set(folded, (counts.get(folded) || 0) + 1);
  });
  const colliding = new Set();
  entries.forEach(({ id, key }) => {
    if (counts.get(foldLabel(key)) > 1) colliding.add(id);
  });
  return colliding;
}

/**
 * The one and only rendering of "this label needs its id". Kept here so every
 * surface that disambiguates does it identically — a user who has learned to
 * read `(#12)` in one list reads it the same way in the next.
 */
export function withIdSuffix(label, id) {
  return `${label} (#${id})`;
}
